import { useParams, Navigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Reveal, SectionHeading } from "../components/primitives";
import { PageHero, CTASection, BookButtons, FAQSection } from "../components/sections";
import { PROGRAM_PAGES, LEGACY_PROGRAM_SLUGS, FAQS } from "../data/site";

const ProgramDetail = () => {
  const { slug } = useParams();
  if (LEGACY_PROGRAM_SLUGS[slug]) return <Navigate to={`/programs/${LEGACY_PROGRAM_SLUGS[slug]}`} replace />;
  const program = PROGRAM_PAGES[slug];
  if (!program) return <Navigate to="/programs" replace />;
  const highlighted = slug === "reaction-dna";

  return (
    <>
      <PageHero
        eyebrow={`DNA Level ${program.num} — ${program.stage}`}
        lines={[`${program.name}.`, program.tagline]}
        text={program.heroText}
      >
        <BookButtons bookTestId={`program-${slug}-book-btn`} waTestId={`program-${slug}-whatsapp-btn`} />
      </PageHero>

      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="The Problem" number="01" title="Why This Level Exists" text={program.problem} />
              <Reveal delay={0.15}>
                <div className={`mt-10 rounded-2xl p-8 ${highlighted ? "border border-dwc-crimson/25 bg-dwc-crimson/[0.03]" : "bg-dwc-soft"}`}>
                  <h3 className="text-sm font-extrabold uppercase tracking-[0.2em] text-dwc-green">Who This Level Is For</h3>
                  <ul className="mt-4 space-y-3">
                    {program.whoFor.map((w) => (
                      <li key={w} className="flex items-start gap-2.5 text-sm text-dwc-charcoal/75">
                        <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${highlighted ? "text-dwc-crimson" : "text-dwc-green"}`} aria-hidden="true" />
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
            <div>
              <SectionHeading eyebrow="The Curriculum" number="02" title="What Is Taught" />
              <ul className="mt-10 space-y-3">
                {program.taught.map((t, i) => (
                  <Reveal key={t} delay={i * 0.04}>
                    <li className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-4 text-sm font-semibold text-dwc-charcoal/80 shadow-[0_4px_16px_rgb(0,0,0,0.03)]">
                      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-black ${highlighted ? "bg-dwc-crimson/10 text-dwc-crimson" : "bg-dwc-soft text-dwc-green"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {t}
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {program.insight && (
        <section className="bg-white pb-24 md:pb-32">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <div className={`rounded-3xl p-10 md:p-14 grain relative overflow-hidden ${highlighted ? "bg-dwc-charcoal text-white" : "bg-dwc-green text-white"}`} data-testid={`dna-insight-${slug}`}>
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-300">What This DNA Really Means</p>
                <h2 className="mt-3 max-w-3xl text-2xl font-extrabold tracking-tight md:text-3xl">{program.insight.title}</h2>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-white/85">{program.insight.intro}</p>
                <ul className="mt-8 grid gap-3 md:grid-cols-2">
                  {program.insight.points.map((pt, i) => (
                    <li key={pt} className="flex items-start gap-3 rounded-xl bg-white/10 p-4 text-sm font-semibold text-white/90">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/15 text-[11px] font-black text-emerald-200">{String(i + 1).padStart(2, "0")}</span>
                      {pt}
                    </li>
                  ))}
                </ul>
                <p className="mt-8 max-w-3xl text-sm leading-relaxed text-emerald-100/90">{program.insight.close}</p>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-dwc-soft py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeading eyebrow="Methodology" number="03" title="How Training Works" text={program.methodology} />
          </div>
          <div>
            <SectionHeading eyebrow="Outcomes" number="04" title="What You'll Walk Away With" />
            <ul className="mt-8 space-y-3">
              {program.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-2.5 text-sm text-dwc-charcoal/75">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />
                  {o}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <FAQSection faqs={FAQS.slice(0, 6)} chapter="05" title="Questions About This Level" />
      <CTASection title="Ready To Start?" sub="Book an appointment and begin at exactly the right level for you." chapter="06" />
    </>
  );
};

export default ProgramDetail;
